import { put, call, takeEvery, takeLatest } from "redux-saga/effects";
import { showModal, hideModal } from "../actions/modal.actions";
import { hideNotification } from "../actions/notifications.actions";
import {
  callSaveSkipDenialReasons,
  callAuthClearSkipDenialReasons,
  setAuthError,
  setAuthRecordCreated,
} from "../actions/authPayment.actions";
import {
  authSubmitSkipDebitPayment,
  authPostSkip,
  authGetSkipLoanInfo,
  authGetSkipEligibility,
  getCardType,
  authVerifySkipPayment,
} from "../utils/requests";

const showErrorNotification = (message) => {
  return {
    type: "SHOW_NOTIFICATION",
    notificationType: "error",
    message: message,
  };
};

const showLoadingModal = () => {
  return showModal("loading", "Loading...");
};

export function* authPostSkipSaga({ debitDetails, skipDetails, hasSubmittedDebit }) {
  try {
    yield put(hideNotification());
    yield put(showLoadingModal());

    let shouldContinue = true;

    if (!hasSubmittedDebit && skipDetails.skipPaymentMethod === "Debit Card") {
      const debitResponse = yield call(authSubmitSkipDebitPayment, { Payment: debitDetails });

      if (debitResponse.PaymentError) {
        shouldContinue = false;
        yield put(showErrorNotification(debitResponse.PaymentError));
      } else {
        // If the debit payment was successful, we need to save the response to the store
        yield put({ type: "AUTH_SKIP_DEBIT_SUBMITTED", skipDebitResponse: debitResponse });
      }
    }

    // Only continue if the debit payment was successful or if the user is paying with an internal account
    if (shouldContinue) {
      const skipResponse = yield call(authPostSkip, skipDetails);

      if (skipResponse.ErrorMessage) {
        yield put(showErrorNotification(skipResponse.ErrorMessage));
      } else {
        yield put({ type: "AUTH_SKIP_SUBMITTED", skipPostResponse: skipResponse });
      }
    }

    yield put(hideModal("loading"));
  } catch (error) {
    // Request didn't work
    yield put(setAuthError());
    yield put(hideModal("loading"));
  }
}

export function* watchAuthPostSkipSaga() {
  yield takeEvery("AUTH_POST_SKIP", authPostSkipSaga);
}

export function* authGetSkipInfoSaga({ loanNumber, setSkipInfoCallback }) {
  try {
    yield put(callAuthClearSkipDenialReasons());
    yield put(hideNotification());
    yield put(showLoadingModal());

    const info = yield call(authGetSkipLoanInfo, loanNumber);

    if (info.PaymentNotAllowedReasonHtml) {
      // Save the denial reasons to the store
      yield put(callSaveSkipDenialReasons([info.PaymentNotAllowedReasonHtml]));

      // This will set the modal status for invalid skip types
      setSkipInfoCallback([info.PaymentNotAllowedReasonHtml]);
    } else {
      const eligibility = yield call(authGetSkipEligibility, loanNumber);

      const combinedInfo = { ...info, ...eligibility };

      if (setSkipInfoCallback) {
        // The setSkipInfoCallback will use state setters from auth parent to either
        // set the modal state for ineligibility or set the payment info state for eligibility
        setSkipInfoCallback(combinedInfo.Message);
      }

      // Since the skip eligibility API has a specific implementation for MEACentral,
      // we need to check for if there are messages in the message property array
      // to determine eligibility rather than rely on the IsEligible boolean on the response

      if (combinedInfo.ErrorOccurred) {
        yield put(callSaveSkipDenialReasons(combinedInfo.Message));
      } else if (combinedInfo.Message.length > 0) {
        // If there are messages, we need to save them to the store
        // for display in the ineligibility modal
        yield put(callSaveSkipDenialReasons(combinedInfo.Message));
      } else {
        yield put({ type: "AUTH_SKIP_INFO_LOADED", selectedLoanSkipInfo: combinedInfo });
        yield put(setAuthRecordCreated());
      }
    }

    yield put(hideModal("loading"));
  } catch (error) {
    // Request didn't work
    yield put(setAuthError());
    yield put(hideModal("loading"));
  }
}

export function* watchAuthGetSkipInfoSaga() {
  yield takeEvery("AUTH_GET_SKIP_INFO", authGetSkipInfoSaga);
}

export function* getCardTypeSaga({ cardNumber }) {
  yield put(hideNotification());
  yield put(showLoadingModal());

  try {
    const skipCardType = yield call(getCardType, cardNumber);
    yield put({ type: "SET_CARD_TYPE", skipCardType });
  } catch (error) {
    // Request didn't work
    // Don't send to error page, fail silently to allow continuing the process
    // And fall back to default card type text in review component
    // yield put(setAuthError());
  }

  yield put(hideModal("loading"));
}

export function* watchGetCardTypeSaga() {
  yield takeLatest("GET_CARD_TYPE", getCardTypeSaga);
}

export function* authVerifySkipPaymentSaga({ paymentDetails, advanceCallback }) {
  try {
    yield put(hideNotification());
    yield put(showLoadingModal());

    const data = yield call(authVerifySkipPayment, paymentDetails);
    yield put(hideModal("loading"));

    // If there is an error, show the notification at the top
    if (data.PaymentError) {
      yield put(showErrorNotification(data.PaymentError));
    } else if(advanceCallback) {
      advanceCallback();
    }
  } catch (error) {
    yield put(setAuthError());
    yield put(hideModal("loading"));
  }
}

export function* watchAuthVerifySkipPaymentSaga() {
  yield takeLatest("AUTH_VERIFY_SKIP_PAYMENT", authVerifySkipPaymentSaga);
}



// WEBPACK FOOTER //
// ./src/sagas/authPaymentSkips.sagas.js